import { getArea, getContent, getPlayer, getPlayerStats } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import { AreaSelect } from "./AreaSelect";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { format, subDays } from "date-fns";
import { formatToUTC } from "@/lib/dateUtils";
import _ from "lodash";
import { SelectedMediaPreview } from "../shared/SelectedMediaPreview";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarIcon, BarChart3, Play, User, AlertCircle, DownloadIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart } from "@/components/ui/bar-chart";
import { AppSelect } from "@/components/ui/app-select";

// Helper function to convert data to CSV
function convertToCSV(headers: string[], rows: any[][]): string {
  const headerRow = headers.map((header) => `"${header.replace(/"/g, '""')}"`).join(",");
  const dataRows = rows.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

  return `${headerRow}\n${dataRows}`;
}

// Helper function to download CSV
function downloadCSV(csvContent: string, fileName: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const ReportPage = () => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [selectedAreaId, setSelectedAreaId] = useState<number>();
  const [dateRangeType, setDateRangeType] = useState<"30d" | "7d" | "1d">("30d");

  const dateRange = {
    from: subDays(new Date(), dateRangeType === "30d" ? 30 : dateRangeType === "7d" ? 7 : 1),
    to: new Date(),
  };

  const startDateFormatted = format(dateRange.from, "dd-MM-yyyy HH:mm");
  const endDateFormatted = format(dateRange.to, "dd-MM-yyyy HH:mm");
  const startDateUTC = formatToUTC(startDateFormatted);
  const endDateUTC = formatToUTC(endDateFormatted);

  console.log(startDateUTC);

  const areas = useQuery({
    queryKey: ["areas", String(activeFirm?.firmGuid)],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
  });

  const areasData = areas.data?.data.result || [];

  const players = useQuery({
    queryKey: ["players", String(activeFirm?.firmGuid)],
    queryFn: () => getPlayer({ pageNumber: 1, numberOfObjectsPerPage: 999999 }, { Firm_Id: activeFirm?.firmGuid }),
  });

  const playersData = players.data?.data.result || [];
  const playersByArea = playersData.filter((player) => player.areaintId === selectedAreaId);

  const getPlayerStatsQuery = useQuery({
    queryKey: ["getPlayerStats", selectedAreaId, startDateUTC, endDateUTC],
    queryFn: async () => {
      const selectedPlayerStatsResponse = await Promise.all(
        playersByArea.map(async (player) => {
          const playerStatsResponse = await getPlayerStats(
            player.intId,
            String(activeFirm?.firmGuid),
            startDateUTC,
            endDateUTC
          );
          return {
            data: playerStatsResponse.data || [],
            playerId: player.intId,
            playerName: playersData.find((p) => p.intId === player.intId)?.name || "",
          };
        })
      );
      return selectedPlayerStatsResponse;
    },
    enabled: !!selectedAreaId && playersByArea.length > 0,
  });

  const isLoading = areas.isLoading || players.isLoading || getPlayerStatsQuery.isLoading;
  const hasError = areas.isError || players.isError || getPlayerStatsQuery.isError;
  const playerStatsData = getPlayerStatsQuery.data;

  const viewsContentIdDict = _.groupBy(
    playerStatsData?.flatMap((player) => player.data.map((stat) => stat)) || [],
    "contentId"
  );

  const mostViewedContentId = Object.keys(viewsContentIdDict).length
    ? Number(_.maxBy(Object.keys(viewsContentIdDict), (contentId) => viewsContentIdDict[contentId].length))
    : undefined;

  const leastViewedContentId = Object.keys(viewsContentIdDict).length
    ? Number(_.minBy(Object.keys(viewsContentIdDict), (contentId) => viewsContentIdDict[contentId].length))
    : undefined;

  const viewsPlayerIdDict = _.groupBy(
    playerStatsData?.flatMap((player) => player.data.map((stat) => stat)) || [],
    "playerId"
  );

  const mostViewedPlayerId = Object.keys(viewsPlayerIdDict).length
    ? Number(_.maxBy(Object.keys(viewsPlayerIdDict), (playerId) => viewsPlayerIdDict[playerId].length))
    : undefined;

  const leastViewedPlayerId = Object.keys(viewsPlayerIdDict).length
    ? Number(_.minBy(Object.keys(viewsPlayerIdDict), (playerId) => viewsPlayerIdDict[playerId].length))
    : undefined;

  const getPlayerName = (playerId: number) => {
    return playersData.find((player) => player.intId === playerId)?.name || "";
  };

  const content = useQuery({
    queryKey: ["content", String(activeFirm?.firmGuid)],
    queryFn: () => getContent({ numberOfObjectsPerPage: 99999999, pageNumber: 1 }, { FirmGuid: activeFirm?.firmGuid }),
  });

  const contentData = content.data?.data.result || [];

  const getContentName = (contentId: number) => {
    return contentData.find((content) => content.intId === contentId)?.name || "";
  };

  // Calculate view counts for charts
  const getViewCountsByContent = () => {
    return Object.entries(viewsContentIdDict).map(([contentId, views]) => ({
      name: getContentName(Number(contentId)),
      value: views.length,
    }));
  };

  const getViewCountsByPlayer = () => {
    return Object.entries(viewsPlayerIdDict).map(([playerId, views]) => ({
      name: getPlayerName(Number(playerId)),
      value: views.length,
    }));
  };

  // Function to handle exporting the report data
  const handleExportReport = () => {
    if (!playerStatsData || playerStatsData.length === 0) return;

    const reportDate = format(new Date(), "yyyy-MM-dd");
    const areaName = areasData.find((area) => area.intId === selectedAreaId)?.name || "unknown-area";
    const fileName = `report-${areaName}-${reportDate}.csv`;

    // Create different exports based on the current active tab
    // For simplicity, we'll export all data

    // Content views data
    const contentHeaders = [t("report.Content Name"), t("report.Views")];
    const contentRows = Object.entries(viewsContentIdDict).map(([contentId, views]) => [
      getContentName(Number(contentId)),
      views.length,
    ]);

    // Player views data
    const playerHeaders = [t("report.Player Name"), t("report.Views")];
    const playerRows = Object.entries(viewsPlayerIdDict).map(([playerId, views]) => [
      getPlayerName(Number(playerId)),
      views.length,
    ]);

    // Combine the data with headers to separate sections
    const summaryHeaders = [t("report.Metric"), t("report.Value")];
    const summaryRows = [
      [t("report.Total Views"), playerStatsData?.flatMap((player) => player.data).length || 0],
      [t("report.Unique Content"), Object.keys(viewsContentIdDict).length],
      [t("report.Analyzed Players"), Object.keys(viewsPlayerIdDict).length],
      [
        t("report.Days Analyzed"),
        Math.round((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)),
      ],
      [t("report.Date Range"), `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`],
      [t("report.Area"), areaName],
    ];

    // Combine all data sections into one CSV
    const csvContent = [
      // Summary section
      `${t("report.Summary")}`,
      convertToCSV(summaryHeaders, summaryRows),
      "", // Empty line as separator

      // Content section
      `${t("report.Content Views")}`,
      convertToCSV(contentHeaders, contentRows),
      "", // Empty line as separator

      // Player section
      `${t("report.Player Views")}`,
      convertToCSV(playerHeaders, playerRows),
    ].join("\n");

    downloadCSV(csvContent, fileName);
  };

  return (
    <PageLayout title={<>{t("report.title")}</>} description={t("report.reportDesc")}>
      <div className="space-y-6">
        {hasError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("report.Error")}</AlertTitle>
            <AlertDescription>
              {t("report.There was an error loading the report data. Please try again.")}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="md:col-span-3">
            <CardHeader>
              <CardTitle>{t("report.Analytics Dashboard")}</CardTitle>
              <CardDescription>{t("report.View detailed analytics for your content and players")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="w-full sm:w-1/3">
                  <label className="text-sm font-medium mb-1 block">{t("report.Select Area")}</label>
                  <AreaSelect onChange={setSelectedAreaId} value={selectedAreaId} />
                </div>
                <div className="w-full sm:w-1/3">
                  <label className="text-sm font-medium mb-1 block">{t("report.Date Range")}</label>
                  <AppSelect
                    value={dateRangeType}
                    options={[
                      {
                        label: "30d",
                        value: "30d",
                      },
                      {
                        label: "7d",
                        value: "7d",
                      },
                      {
                        label: "1d",
                        value: "1d",
                      },
                    ]}
                    onChange={(value) => {
                      setDateRangeType(value as "30d" | "7d" | "1d");
                    }}
                    clearable={false}
                  />
                </div>
              </div>

              {!selectedAreaId && (
                <div className="rounded-lg border p-6 text-center bg-muted/50">
                  <div className="flex justify-center mb-4">
                    <BarChart3 className="h-12 w-12 text-muted-foreground/60" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{t("report.Select an area to view analytics")}</h3>
                  <p className="text-muted-foreground">
                    {t("report.Choose an area from the dropdown to load the report data")}
                  </p>
                </div>
              )}

              {selectedAreaId && isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-[200px] w-full rounded-lg" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Skeleton className="h-[100px] rounded-lg" />
                    <Skeleton className="h-[100px] rounded-lg" />
                  </div>
                </div>
              ) : null}

              {selectedAreaId && !isLoading && (!playerStatsData || playerStatsData.length === 0) ? (
                <div className="rounded-lg border p-6 text-center bg-muted/50">
                  <div className="flex justify-center mb-4">
                    <AlertCircle className="h-12 w-12 text-muted-foreground/60" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{t("report.No data available")}</h3>
                  <p className="text-muted-foreground">
                    {t("report.No player statistics found for the selected area and time period")}
                  </p>
                </div>
              ) : null}

              {selectedAreaId && !isLoading && playerStatsData && playerStatsData.length > 0 ? (
                <Tabs defaultValue="overview" className="mt-4">
                  <TabsList className="mb-4">
                    <TabsTrigger value="overview">
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {t("report.Overview")}
                    </TabsTrigger>
                    <TabsTrigger value="content">
                      <Play className="h-4 w-4 mr-2" />
                      {t("report.Content Analysis")}
                    </TabsTrigger>
                    <TabsTrigger value="players">
                      <User className="h-4 w-4 mr-2" />
                      {t("report.Player Analysis")}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{t("report.Top viewed content")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {mostViewedContentId ? (
                            <div className="space-y-4">
                              <SelectedMediaPreview contentIntId={mostViewedContentId} />
                              <div className="bg-muted/30 px-3 py-1 rounded-full text-sm inline-flex items-center">
                                <BarChart3 className="h-4 w-4 mr-2" />
                                {viewsContentIdDict[mostViewedContentId]?.length || 0} {t("report.views")}
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted-foreground py-8 text-center">{t("report.No content found")}</div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{t("report.Least viewed content")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {leastViewedContentId ? (
                            <div className="space-y-4">
                              <SelectedMediaPreview contentIntId={leastViewedContentId} />
                              <div className="bg-muted/30 px-3 py-1 rounded-full text-sm inline-flex items-center">
                                <BarChart3 className="h-4 w-4 mr-2" />
                                {viewsContentIdDict[leastViewedContentId]?.length || 0} {t("report.views")}
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted-foreground py-8 text-center">{t("report.No content found")}</div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{t("report.Engagement Summary")}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="bg-muted/30 p-4 rounded-lg">
                            <div className="text-sm font-medium text-muted-foreground mb-1">
                              {t("report.Total Views")}
                            </div>
                            <div className="text-2xl font-bold">
                              {playerStatsData?.flatMap((player) => player.data).length || 0}
                            </div>
                          </div>
                          <div className="bg-muted/30 p-4 rounded-lg">
                            <div className="text-sm font-medium text-muted-foreground mb-1">
                              {t("report.Unique Content")}
                            </div>
                            <div className="text-2xl font-bold">{Object.keys(viewsContentIdDict).length}</div>
                          </div>
                          <div className="bg-muted/30 p-4 rounded-lg">
                            <div className="text-sm font-medium text-muted-foreground mb-1">
                              {t("report.Analyzed Players")}
                            </div>
                            <div className="text-2xl font-bold">{Object.keys(viewsPlayerIdDict).length}</div>
                          </div>
                          <div className="bg-muted/30 p-4 rounded-lg">
                            <div className="text-sm font-medium text-muted-foreground mb-1">
                              {t("report.Days Analyzed")}
                            </div>
                            <div className="text-2xl font-bold">
                              {Math.round((dateRange.to.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24))}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="content" className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t("report.Content Views Distribution")}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(viewsContentIdDict).length > 0 ? (
                          <BarChart
                            data={getViewCountsByContent()}
                            index="name"
                            categories={["value"]}
                            valueFormatter={(value: number) => `${value} views`}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            {t("report.No content view data available")}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="players" className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{t("report.Most Active Player")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {mostViewedPlayerId ? (
                            <div className="space-y-2">
                              <div className="flex items-center">
                                <User className="h-10 w-10 p-1 mr-3 rounded-full bg-primary/10 text-primary" />
                                <div>
                                  <div className="font-medium">{getPlayerName(mostViewedPlayerId)}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {viewsPlayerIdDict[mostViewedPlayerId]?.length || 0} {t("report.views")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted-foreground py-4 text-center">{t("report.No player found")}</div>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">{t("report.Least Active Player")}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {leastViewedPlayerId ? (
                            <div className="space-y-2">
                              <div className="flex items-center">
                                <User className="h-10 w-10 p-1 mr-3 rounded-full bg-muted text-muted-foreground" />
                                <div>
                                  <div className="font-medium">{getPlayerName(leastViewedPlayerId)}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {viewsPlayerIdDict[leastViewedPlayerId]?.length || 0} {t("report.views")}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-muted-foreground py-4 text-center">{t("report.No player found")}</div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    <Card>
                      <CardHeader>
                        <CardTitle>{t("report.Player Engagement")}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {Object.keys(viewsPlayerIdDict).length > 0 ? (
                          <BarChart
                            data={getViewCountsByPlayer()}
                            index="name"
                            categories={["value"]}
                            valueFormatter={(value: number) => `${value} views`}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full text-muted-foreground">
                            {t("report.No player view data available")}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : null}
            </CardContent>
          </Card>

          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">{t("report.Report Settings")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="text-sm font-medium mb-1">{t("report.Areas")}</div>
                <div className="text-sm text-muted-foreground">
                  {areasData.length} {t("report.areas available")}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">{t("report.Players")}</div>
                <div className="text-sm text-muted-foreground">
                  {playersData.length} {t("report.total players")}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-1">{t("report.Date Range")}</div>
                <div className="text-sm text-muted-foreground flex items-center">
                  <CalendarIcon className="h-3.5 w-3.5 mr-1" />
                  {format(dateRange.from, "MMM d, yyyy")} - {format(dateRange.to, "MMM d, yyyy")}
                </div>
              </div>
              <Button
                variant="secondary"
                className="w-full mt-4"
                onClick={handleExportReport}
                disabled={!selectedAreaId || isLoading || hasError}
              >
                <DownloadIcon className="h-4 w-4 mr-2" />
                {t("report.Export Report")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};
